import React, {useMemo, useState} from 'react';

import {SignoffFooterPanel} from 'app/components/general/GenericDocumentActionButtons/SignoffRequestModal/SignoffFooterPanel';
import {SignoffTeams} from 'app/components/general/GenericDocumentActionButtons/SignoffRequestModal/SignoffTeams';
import {StatusPanel} from 'app/components/general/GenericDocumentActionButtons/SignoffRequestModal/StatusPanel';
import {getCommonDocument} from 'app/components/general/GenericDocumentActionButtons/SignoffRequestModal/utils';
import TabbedModal from 'app/components/general/TabbedModal';
import {createFormErrorsContext, type FormErrorsProps} from 'app/general/forms';
import {useSignoffTemplates} from 'app/signoffs/api';
import {UNLIMITED_PAGE_SIZE} from 'app/utils/constants';
import {getContentType} from 'app/utils/content-type';
import {gt} from 'app/utils/i18n';

import type {BaseActionableDocumentType} from 'app/general/types';

type OwnProps = {
  contentType: 'procedure' | 'policy' | 'proceduregroup';
  document: BaseActionableDocumentType;
  onClose: () => void;
  onSuccess: (doc?: BaseActionableDocumentType) => unknown;
  isOpen: boolean;
};
type Props = OwnProps & FormErrorsProps;

function BaseSignoffRequestModal({
  document,
  contentType,
  onClose,
  isOpen,
  onSuccess,
  captureFormErrorsFromRejection,
}: Props) {
  const ct = getContentType(contentType);
  const [status, setStatus] = useState<'start' | 'success' | 'error'>('start');

  // The Sign Off templates tell us which of the attached teams have the
  // "When managers choose" rule enabled. Scoped to this document's teams via
  // team_ids so we never fetch templates for unrelated teams.
  const documentTeamIds = getCommonDocument(document)
    .team_memberships.map((t) => t.team_id)
    .sort()
    .join(',');

  const {data: signoffTemplates, isLoading} = useSignoffTemplates({
    page: '1',
    page_size: UNLIMITED_PAGE_SIZE,
    ordering: 'team__name',
    team_ids: documentTeamIds,
  });

  const signoffEnabledTeamUrls = useMemo(() => {
    if (signoffTemplates == null) {
      return new Set<string>();
    }

    return new Set(
      signoffTemplates.results
        .filter((template) => template.is_trigger_on_signoff_requested)
        .map((template) => template.team)
    );
  }, [signoffTemplates]);

  return (
    <TabbedModal
      show={isOpen}
      onCancel={onClose}
      tabs={[
        {
          eventKey: 'main-tab',
          name: gt('Require Sign Off for this version'),
          icon: ct.icon,
          content: (
            <>
              {status === 'start' ? (
                <div className="mb-2">
                  <StatusPanel
                    isLoading={isLoading}
                    document={document}
                    disabled={signoffEnabledTeamUrls.size === 0}
                  />
                  <div>
                    {!isLoading ? (
                      <SignoffTeams
                        document={document}
                        contentType={contentType}
                        signoffEnabledTeamUrls={signoffEnabledTeamUrls}
                      />
                    ) : null}
                  </div>
                </div>
              ) : status === 'error' ? (
                <div className="alert alert-danger my-1">
                  {gt('Request failed. Sign Offs were not requested.')}
                </div>
              ) : null}
              <SignoffFooterPanel
                document={document}
                contentType={contentType}
                status={status}
                onClose={onClose}
                onSuccess={onSuccess}
                disabled={signoffEnabledTeamUrls.size === 0}
                onError={() => setStatus('error')}
                onBadRequest={(err) => {
                  setStatus('start');
                  captureFormErrorsFromRejection(err);
                }}
              />
            </>
          ),
        },
      ]}
    />
  );
}

export const SignoffRequestModal = createFormErrorsContext(
  BaseSignoffRequestModal
);
